
import {
  endorsementMapActions,
  endorsementMapGetters
} from "@/store/modules/endorsements";

import { objectDifference } from "@/helpers";
import VueWithMixins from "@/helpers/mixins";
import EndorsementMixin from "./EndorsementMixin";
import { IEndorsement } from "@/store/modules/endorsements/types";

export default VueWithMixins(EndorsementMixin).extend({
  name: "EndorsementGroups",

  async created() {
    try {
      const { data: endorsement = {} } = await this.getEndorsement(
        `${this.endorsementId}?grouped=true`
      );
      if (!endorsement || endorsement.status === "rejected") {
        this.errorMessage = this.resourceNotFoundMsg =
          endorsement?.message || "Error retrieving endorsement data";
        this.resourceNotFound = true;
      } else {
        this.SET_EDIT(endorsement);
        await this.getGroupedEndorsements();
      }

      this.initialized = true;
    } catch (error) {
      this.initialized = true;
      this.$bugSnagClient.notify(error);
    }
  },
  computed: {
    endorsementGroupOptions(): { label: string; value: string }[] {
      return this.allEndorsements
        ?.filter(endorsement => {
          return this.endorsementId
            ? endorsement._id !== this.endorsementId
            : true;
        })
        ?.map((option: IEndorsement) => {
          return {
            label: this.formatEndorsementName(option),
            value: option._id
          };
        });
    },

    parentEndorsement(): IEndorsement | undefined {
      return this.allEndorsements?.find((group: IEndorsement) =>
        group.endorsementGroups?.includes(this.endorsementId)
      );
    },
    endorsementIsAlreadyGrouped(): boolean {
      return Boolean(this.parentEndorsement);
    },
    updatedFields(): any {
      const edited = {
        endorsementGroups: this.editingEndorsement?.endorsementGroups
      };
      const original = {
        endorsementGroups: this.endorsement?.endorsementGroups
      };

      return edited && original ? objectDifference(edited, original) : {};
    },
    disablePrimaryToolbar(): boolean {
      return Object.keys(this.updatedFields).length === 0;
    },

    formLabel(): string {
      return `Select Endorsement Groups for ${this.formatEndorsementName(
        this.editing
      ) || "N/A"}`;
    },
    ...endorsementMapGetters(["getEndorsementById", "editingEndorsement"]),
    endorsement(): any {
      return this.getEndorsementById(this.endorsementId);
    },
    topActionButtonSecondary() {
      return {
        text: "Cancel",
        key: "cancel"
      };
    },
    hasGroupedEndorsements(): boolean {
      return Boolean(this.editing?.endorsementGroups?.length);
    },

    specialEndorsementQuery(): Record<string, any> {
      return {
        _id__in: [
          ...(this.editing?.endorsementGroups || []),
          this.editing?._id || null
        ].filter(Boolean),
        endorsementGroups__in: [this.editing?._id],
        grouped: true
      };
    }
  },
  methods: {
    ...endorsementMapActions([
      "getEndorsement",
      "getAllEndorsements",
      "updateEndorsement"
    ]),
    handleFormFieldChange(value: string[]) {
      this.editField({ key: "endorsementGroups", value });
    },
    async remoteMethod(searchTerm: string) {
      if (searchTerm.trim().length === 0 || searchTerm.trim().length < 3) {
        return;
      }
      try {
        this.loading = true;
        const response = await this.getAllEndorsements({
          query: {
            __query: searchTerm,
            appliesTo__in: this.editing?.appliesTo,
            formUsage: this.editing?.formUsage
          }
        });
        if (response && response.length) {
          const currentEndorsement = this.allEndorsements.find(
            (endorsement: IEndorsement) =>
              endorsement._id === this.endorsementId
          );
          if (!currentEndorsement && this.editing) {
            this.setItem([
              ...(this.allEndorsements || []),
              this.editing as IEndorsement
            ]);
          }
        }
      } catch (error) {
        this.$message({
          type: "error",
          message: "Error fetching documents"
        });
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = true;
      }
    },
    formatEndorsementName(endorsement: Partial<IEndorsement> | null): string {
      return `${endorsement?.formNumber || "N/A"} - ${endorsement?.formName ||
        "N/A"}`;
    },
    async getGroupedEndorsements() {
      try {
        await this.getAllEndorsements({
          query: this.specialEndorsementQuery
        });
      } catch (error) {
        this.$bugSnagClient.notify(error);
      }
    },
    async cancelCallback(): Promise<any> {
      this.$router
        .push(`/admin/endorsements/${this.endorsementId}/edit`)
        .catch(() => {});
    },
    onToolbarItemClick(event: string) {
      switch (event) {
        case "cancel":
          this.cancelCallback();
          break;
        case "update":
          this.submit();
          break;
      }
    },

    async submit(): Promise<void> {
      try {
        await this.updateEndorsement({
          update: {
            endorsementGroups: this.editing?.endorsementGroups,
            action: "update",
            endorsementIds: [this.endorsementId]
          }
        });
        this.successMessage = "Endorsement groups updated";
        this.cancelCallback();
      } catch (error) {
        this.errorMessage = error.message;
      }
    }
  }
});
